import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { ScrollText, ExternalLink } from 'lucide-react';
import axios from 'axios';
import { API } from '@/App';
import { toast } from 'sonner';

/**
 * Global blocking modal that forces users to re-accept legal documents that have
 * been updated (version bump) or newly apply to them (tier/role change). Mounted
 * once inside the dashboard layout.
 */
const LegalReAckModal = () => {
  const [pending, setPending] = useState([]);
  const [agreed, setAgreed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) return;
        const res = await axios.get(`${API}/legal/acknowledgements/pending`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (active && res.data?.pending?.length) {
          setPending(res.data.pending);
          setOpen(true);
        }
      } catch (error) {
        /* silent — never block the app on a check failure */
      }
    })();
    return () => { active = false; };
  }, []);

  const handleAccept = async () => {
    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      for (const doc of pending) {
        await axios.post(
          `${API}/legal/acknowledgements/accept`,
          { document_type: doc.document_type },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }
      toast.success('Thank you — your agreements are up to date.');
      setOpen(false);
      setPending([]);
    } catch (error) {
      toast.error('Could not record your acknowledgement. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (!open || !pending.length) return null;

  // A brand-new member (an invited practitioner, say) has never accepted anything — telling them
  // we've "updated" documents they have never seen reads as nonsense.
  const firstTime = pending.every((d) => d.reason === 'never_accepted');

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
      data-testid="legal-reack-modal"
    >
      <div className="bg-white rounded-2xl max-w-lg w-full p-8 shadow-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center gap-3 mb-4">
          <ScrollText className="w-7 h-7 text-cyan-600" />
          <h2 className="text-2xl font-bold" style={{ color: '#331a67' }}>
            {firstTime ? 'Before you begin' : 'Updated Agreements'}
          </h2>
        </div>
        <p className="text-gray-700 mb-4">
          {firstTime
            ? 'Please read and agree to the following. You only need to do this once.'
            : 'We have updated the following. What changed is listed under each one.'}
        </p>
        <ul className="space-y-2 mb-6">
          {pending.map((doc) => (
            <li
              key={doc.document_type}
              className="p-3 rounded-lg bg-gray-50 border border-gray-200"
            >
              <div className="flex items-center justify-between gap-3">
                <span className="font-medium text-gray-800">
                  {doc.title} <span className="text-xs text-gray-500">v{doc.version}</span>
                </span>
                <a
                  href={doc.route}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-cyan-600 hover:text-cyan-700 inline-flex items-center gap-1 text-sm whitespace-nowrap"
                  data-testid={`legal-reack-review-${doc.document_type}`}
                >
                  Review <ExternalLink className="w-3.5 h-3.5" />
                </a>
              </div>

              {doc.changes?.length > 0 && (
                <div className="mt-3 pt-3 border-t border-gray-200"
                  data-testid={`legal-reack-changes-${doc.document_type}`}>
                  <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">
                    What changed since v{doc.accepted_version}
                  </p>
                  <ul className="space-y-1.5 max-h-48 overflow-y-auto pr-1">
                    {doc.changes.map((change, i) => (
                      <li key={i} className="text-sm leading-snug">
                        {change.section && change.section !== change.text
                          && change.section !== doc.changes[i - 1]?.section && (
                          <span className="block text-[11px] text-gray-500 mt-1.5">{change.section}</span>
                        )}
                        <span className={change.change === 'added'
                          ? 'text-emerald-700'
                          : 'text-rose-700'}>
                          {change.change === 'added' ? '+ ' : '– '}
                          <span className={change.change === 'added' ? '' : 'line-through decoration-rose-300'}>
                            {change.text}
                          </span>
                        </span>
                      </li>
                    ))}
                  </ul>
                  {doc.changes_truncated && (
                    <p className="text-xs text-gray-500 mt-2" data-testid={`legal-reack-more-${doc.document_type}`}>
                      More was changed than fits here — use Review to read the full document.
                    </p>
                  )}
                </div>
              )}
            </li>
          ))}
        </ul>
        <label className="flex items-start gap-3 mb-6 cursor-pointer">
          <Checkbox
            checked={agreed}
            onCheckedChange={(v) => setAgreed(!!v)}
            data-testid="legal-reack-agree-checkbox"
          />
          <span className="text-sm text-gray-700">
            I have read and agree to the documents listed above.
          </span>
        </label>
        <Button
          onClick={handleAccept}
          disabled={!agreed || submitting}
          className="w-full bg-cyan-600 hover:bg-cyan-700 text-white"
          data-testid="legal-reack-accept-btn"
        >
          {submitting ? 'Saving…' : 'Agree & Continue'}
        </Button>
      </div>
    </div>
  );
};

export default LegalReAckModal;
